import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import axiosInstance from '../../../utils/axios';
import { toast } from 'react-hot-toast';

const AuditProgressDashboard = ({ onRefresh }) => {
  const [loading, setLoading] = useState(false);
  const [progressData, setProgressData] = useState({
    overall: null,
    departments: [],
    categories: [],
    stats: {},
  });
  const [financialStats, setFinancialStats] = useState({
    total_valuation: 0,
    verified_valuation: 0,
    exception_valuation: 0,
  });

  const loadProgress = useCallback(async () => {
    try {
      setLoading(true);
      const [progRes, statsRes] = await Promise.all([
        axiosInstance.get('/audit/progress'),
        axiosInstance.get('/audit/stats'),
      ]);

      if (progRes.data.success) {
        const data = progRes.data.data;
        setProgressData({
          overall: data.overall,
          departments: data.departments || [],
          categories: data.categories || [],
          stats: data.stats || {},
        });
      }

      if (statsRes.data.success) {
        setFinancialStats({
          total_valuation: statsRes.data.data.total_valuation || 0,
          verified_valuation: statsRes.data.data.verified_valuation || 0,
          exception_valuation: statsRes.data.data.exception_valuation || 0,
        });
      }
    } catch (error) {
      console.error('Error loading audit progress:', error);
      toast.error('Failed to load audit progress');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProgress();
  }, [loadProgress]);

  // Listen for refresh events
  useEffect(() => {
    const handleRefresh = () => {
      loadProgress();
      if (onRefresh) onRefresh();
    };
    window.addEventListener('refreshAuditView', handleRefresh);
    window.addEventListener('refreshAuditStats', handleRefresh);
    return () => {
      window.removeEventListener('refreshAuditView', handleRefresh);
      window.removeEventListener('refreshAuditStats', handleRefresh);
    };
  }, [loadProgress, onRefresh]);

  const formatINR = (amount) => {
    return '₹' + Number(amount || 0).toLocaleString('en-IN', {
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
    });
  };

  if (loading && !progressData.overall) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-3"></div>
        <p className="text-sm text-muted-foreground">Calculating quarterly audit progress...</p>
      </div>
    );
  }

  const mainProgress = progressData.overall || {
    total_assets: 0,
    verified_assets: 0,
    pending_assets: 0,
    exception_assets: 0,
    escalated_assets: 0,
    completion_percentage: 0,
  };

  const progressCards = [
    {
      id: 'overall',
      title: 'Overall Audit Progress',
      completed: mainProgress.verified_assets || 0,
      total: mainProgress.total_assets || 0,
      percentage: mainProgress.completion_percentage || 0,
      color: 'bg-primary',
      textColor: 'text-primary',
      icon: 'Target',
    },
    {
      id: 'departments',
      title: 'Department Coverage',
      completed: progressData.departments?.filter((d) => d.percentage >= 100).length || 0,
      total: progressData.departments?.length || 0,
      percentage:
        progressData.departments?.length > 0
          ? Math.round(
              (progressData.departments?.filter((d) => d.percentage >= 100).length /
                progressData.departments?.length) *
                100
            )
          : 0,
      color: 'bg-indigo-600',
      textColor: 'text-indigo-600',
      icon: 'Building2',
    },
    {
      id: 'categories',
      title: 'Asset Type Coverage',
      completed: progressData.categories?.filter((c) => c.percentage >= 100).length || 0,
      total: progressData.categories?.length || 0,
      percentage:
        progressData.categories?.length > 0
          ? Math.round(
              (progressData.categories?.filter((c) => c.percentage >= 100).length /
                progressData.categories?.length) *
                100
            )
          : 0,
      color: 'bg-emerald-600',
      textColor: 'text-emerald-600',
      icon: 'Package',
    },
    {
      id: 'exceptions',
      title: 'Verification Integrity',
      completed:
        mainProgress.total_assets - mainProgress.exception_assets - mainProgress.escalated_assets ||
        0,
      total: mainProgress.total_assets || 0,
      percentage:
        mainProgress.total_assets > 0
          ? Math.round(
              ((mainProgress.total_assets -
                mainProgress.exception_assets -
                mainProgress.escalated_assets) /
                mainProgress.total_assets) *
                100
            )
          : 100,
      color: 'bg-amber-600',
      textColor: 'text-amber-600',
      icon: 'ShieldCheck',
    },
  ];

  return (
    <div className="space-y-6">
      {/* 1. Header Banner & Cycle Highlights */}
      <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border border-primary/20 rounded-xl p-5 flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center space-x-3.5">
          <div className="w-12 h-12 rounded-xl bg-primary text-white flex items-center justify-center shadow-md">
            <Icon name="Award" size={26} />
          </div>
          <div>
            <div className="flex items-center space-x-2">
              <h2 className="text-lg font-bold text-foreground">
                Quarterly Asset Audit Cycle - {progressData.stats?.quarter || 'Q4 2026'}
              </h2>
              <span className="bg-emerald-500/10 text-emerald-600 text-xs px-2.5 py-0.5 rounded-full font-semibold">
                SOX / Internal Ready
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              Target Completion: {progressData.stats?.target_completion_date || '31 Dec 2026'} • Full compliance and physical custody tracking
            </p>
          </div>
        </div>

        {/* Financial Valuation Summary in Indian Currency */}
        <div className="flex items-center space-x-4 border-l border-border pl-4 flex-wrap gap-3">
          <div>
            <div className="text-[11px] font-medium text-muted-foreground">Total Inventory Under Audit</div>
            <div className="text-base font-bold font-mono text-foreground">
              {formatINR(financialStats.total_valuation)}
            </div>
          </div>
          <div>
            <div className="text-[11px] font-medium text-emerald-600">Verified Valuation</div>
            <div className="text-base font-bold font-mono text-emerald-600">
              {formatINR(financialStats.verified_valuation)}
            </div>
          </div>
          {financialStats.exception_valuation > 0 && (
            <div>
              <div className="text-[11px] font-medium text-rose-600">At Risk (Exceptions)</div>
              <div className="text-base font-bold font-mono text-rose-600">
                {formatINR(financialStats.exception_valuation)}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 2. Main KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {progressCards.map((item) => (
          <div key={item.id} className="bg-card border border-border rounded-xl p-5 shadow-subtle">
            <div className="flex items-center justify-between mb-3">
              <div className={`w-9 h-9 ${item.color}/10 ${item.textColor} rounded-lg flex items-center justify-center`}>
                <Icon name={item.icon} size={20} />
              </div>
              <span className="text-2xl font-bold font-mono text-card-foreground">
                {item.percentage}%
              </span>
            </div>
            <h3 className="text-sm font-semibold text-card-foreground mb-1">{item.title}</h3>
            <div className="flex items-center justify-between text-xs text-muted-foreground mb-2.5">
              <span>
                {item.completed} of {item.total} units
              </span>
              <span>{Math.max(0, item.total - item.completed)} remaining</span>
            </div>
            <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
              <div
                className={`${item.color} h-2 rounded-full transition-all duration-500`}
                style={{ width: `${Math.min(item.percentage, 100)}%` }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* 3. Department Progress Detail */}
      {progressData.departments && progressData.departments.length > 0 && (
        <div className="bg-card border border-border rounded-xl p-6 shadow-subtle">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-base font-bold text-card-foreground">Department Verification Breakdown</h3>
              <p className="text-xs text-muted-foreground">Audit completion rates across functional company units</p>
            </div>
            <span className="text-xs text-muted-foreground">
              {progressData.departments.length} Active Departments
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {progressData.departments.map((dept, index) => (
              <div key={index} className="p-3.5 border border-border rounded-lg bg-muted/10">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-semibold text-card-foreground">{dept.name}</span>
                  <span className="text-xs font-mono font-medium text-foreground">
                    {dept.verified} / {dept.total} ({dept.percentage}%)
                  </span>
                </div>
                <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                  <div
                    className={`h-2 rounded-full transition-all duration-500 ${
                      dept.percentage >= 100
                        ? 'bg-emerald-500'
                        : dept.percentage >= 50
                        ? 'bg-primary'
                        : 'bg-amber-500'
                    }`}
                    style={{ width: `${Math.min(dept.percentage, 100)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 4. Category Progress Detail */}
      {progressData.categories && progressData.categories.length > 0 && (
        <div className="bg-card border border-border rounded-xl p-6 shadow-subtle">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-base font-bold text-card-foreground">Equipment & Category Progress</h3>
              <p className="text-xs text-muted-foreground">Audit status grouped by hardware classification</p>
            </div>
            <span className="text-xs text-muted-foreground">
              {progressData.categories.length} Asset Types
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
            {progressData.categories.map((category, index) => (
              <div key={index} className="p-4 border border-border rounded-lg bg-card hover:bg-muted/10 transition">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-xs text-card-foreground">{category.name}</span>
                  <span className="text-xs font-bold font-mono text-primary">{category.percentage}%</span>
                </div>
                <div className="w-full bg-muted rounded-full h-1.5 overflow-hidden mb-2">
                  <div
                    className="bg-primary h-1.5 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min(category.percentage, 100)}%` }}
                  />
                </div>
                <div className="flex justify-between text-[11px] text-muted-foreground">
                  <span className="text-emerald-600 font-medium">Verified: {category.verified}</span>
                  <span className="text-amber-600 font-medium">Pending: {category.pending}</span>
                  <span>Total: {category.total}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditProgressDashboard;
